/**
 * STORY-298: the chart payload a chart answer carries (`chart_data` on the
 * response, `metadata.chartData` once stored), checked before anything draws
 * it or hides text for it.
 *
 * The metadata comes back from the session store as the client sent it, so a
 * chart is drawn only when its shape is one InteractiveChart can draw; any
 * other entry is skipped — and, since only drawn charts hide their text block,
 * a skipped chart leaves its text on screen (fail open).
 */
import type { ChartPayload, ChartSeries } from '@/types';

const KINDS = new Set(['line', 'bar', 'histogram']);

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

function normalizeSeries(raw: unknown): ChartSeries | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Record<string, unknown>;
  if (typeof s.label !== 'string' || !Array.isArray(s.values)) return null;
  const values = s.values.map((v) => (isNum(v) ? v : null));
  const out: ChartSeries = { label: s.label, values };
  for (const k of ['mean', 'min', 'max'] as const) if (isNum(s[k])) out[k] = s[k] as number;
  return out;
}

export function normalizeChart(raw: unknown): ChartPayload | null {
  if (!raw || typeof raw !== 'object') return null;
  const c = raw as Record<string, unknown>;
  if (typeof c.kind !== 'string' || !KINDS.has(c.kind)) return null;
  if (typeof c.title !== 'string' || !Array.isArray(c.series)) return null;
  const series = c.series.map(normalizeSeries);
  if (series.length === 0 || series.some((s) => s === null)) return null;
  const chart: ChartPayload = {
    kind: c.kind as ChartPayload['kind'],
    title: c.title,
    series: series as ChartSeries[],
  };
  if (typeof c.period === 'string') chart.period = c.period;
  if (typeof c.unit === 'string') chart.unit = c.unit;
  if (typeof c.text_preview === 'string') chart.text_preview = c.text_preview;
  if (chart.kind === 'histogram') {
    const edges = c.edges;
    if (!Array.isArray(edges) || !edges.every(isNum)) return null;
    if (chart.series.some((s) => s.values.length !== edges.length - 1)) return null;
    chart.edges = edges as number[];
    // what a bucket counts; absent or unknown reads as points
    if (c.count_unit === 'hosts' || c.count_unit === 'points') chart.count_unit = c.count_unit;
  } else if (
    Array.isArray(c.x) && c.x.every(isNum)
    && chart.series.every((s) => s.values.length === (c.x as number[]).length)
  ) {
    // a time axis only when every series has one value per timestamp
    chart.x = c.x as number[];
  }
  const t = c.truncated as Record<string, unknown> | undefined;
  if (t && isNum(t.shown) && isNum(t.total)) chart.truncated = { shown: t.shown, total: t.total };
  const d = c.downsampled as Record<string, unknown> | undefined;
  if (d && isNum(d.from) && isNum(d.to)) chart.downsampled = { from: d.from, to: d.to };
  return chart;
}

/** The drawable charts of a message, in order; [] for none or a malformed list. */
export function drawableCharts(raw: unknown): ChartPayload[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeChart).filter((c): c is ChartPayload => c !== null);
}

/** A rounded-up axis maximum: 1, 2, 2.5, 5 or 10 times a power of ten. */
export function niceMax(v: number): number {
  if (!(v > 0)) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 2.5, 5, 10]) if (v <= m * p) return m * p;
  return 10 * p;
}

/** A value as the text chart prints it: integers bare, else at most 2 decimals. */
export function formatValue(v: number): string {
  if (Number.isInteger(v)) return String(v);
  return v.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}
